import React, { useEffect } from "react";
import { useState } from "react";
import useSwipe, { SwipeDirection } from "../../helpers/touch/useSwipe";
import useValue from "../../utils/useValue";
import MaskShapeContainer, {
  MaskType,
} from "../MaskShapeContainer/MaskShapeContainer";

interface IBaseCarousel {
  slides: JSX.Element[];
  slideSelectorColor: string;
  selectedSlideSelectorColor: string;
  leftSlideButton: React.ReactNode;
  rightSlideButton: React.ReactNode;
  showSlideButtons: boolean;
  showSelectorButtons: boolean;
  height: string;
  width: string;
  selectorButtonsBottomPosition?: string;
  mask?: React.SVGAttributes<SVGElement>;
  maskClipPathId?: string;
  maskType?: MaskType;
}

const BaseCarousel = ({
  slides,
  slideSelectorColor,
  selectedSlideSelectorColor,
  leftSlideButton,
  rightSlideButton,
  showSlideButtons,
  showSelectorButtons,
  height,
  width,
  selectorButtonsBottomPosition,
  mask,
  maskClipPathId,
  maskType,
}: IBaseCarousel) => {
  const [currentSlide, setCurrentSlide] = useState<number>(0);
  const { setValue } = useValue<number>("currentSlide", 0);

  const previousSlide = () =>
    setCurrentSlide((currentSlide) =>
      currentSlide === 0 ? slides.length - 1 : currentSlide - 1
    );

  const nextSlide = () =>
    setCurrentSlide((currentSlide) =>
      currentSlide === slides.length - 1 ? 0 : currentSlide + 1
    );

  const goToSlide = (index: number) => setCurrentSlide(index);

  const swipeHandlers = useSwipe({
    swipeDirection: SwipeDirection.Horizontal,
    onSwipeLeftOrUp: nextSlide,
    onSwipeRightOrDown: previousSlide,
  });

  useEffect(() => {
    setValue(currentSlide);
  }, [currentSlide]);

  const renderChildren = (): JSX.Element => (
    <>
      <div className="h-full" style={{ clipPath: `url(#${maskClipPathId})` }}>
        <div
          className="h-full flex flex-row transition-transform ease-out duration-500"
          style={{
            transform: `translateX(-${currentSlide * 100}%`,
          }}
        >
          {slides.map((slide: JSX.Element, index: number) => (
            <div
              className={`w-full h-full grow-0 shrink-0 carousel-slide ${
                currentSlide === index ? "slide-current" : ""
              }`}
              key={index}
            >
              {slide}
            </div>
          ))}
        </div>
      </div>
      {showSlideButtons && slides.length > 1 && (
        <>
          <div className="absolute h-full w-fit top-0 left-0 flex items-center">
            <button type="button" onClick={previousSlide}>
              {leftSlideButton}
            </button>
          </div>

          <div className="absolute h-full w-fit top-0 right-0 flex items-center">
            <button type="button" onClick={nextSlide}>
              {rightSlideButton}
            </button>
          </div>
        </>
      )}
      {showSelectorButtons && slides.length > 1 && (
        <div
          className={`absolute w-full h-[1.25rem] ${
            selectorButtonsBottomPosition ?? "bottom-0"
          } flex justify-center items-center`}
        >
          {slides.map((slide: JSX.Element, index: number) => (
            <div
              onClick={() => goToSlide(index)}
              key={index}
              className={`transition-[width,background] duration-500 delay-100 h-[0.625rem] m-0.5 ${
                currentSlide === index
                  ? `w-[1.25rem] ${selectedSlideSelectorColor}`
                  : `w-[0.625rem] ${slideSelectorColor}`
              } cursor-pointer rounded-full`}
            />
          ))}
        </div>
      )}
    </>
  );

  return maskType ? (
    <MaskShapeContainer
      {...swipeHandlers}
      className={`${height} ${width} relative overflow-hidden touch-pan-y`}
    >
      {renderChildren()}
    </MaskShapeContainer>
  ) : (
    <>
      <div
        {...swipeHandlers}
        className={`${height} ${width} relative overflow-hidden touch-pan-y`}
      >
        {renderChildren()}
      </div>
      {mask}
    </>
  );
};

export default BaseCarousel;
